import Vue from "vue";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import Guid from "../../helpers/guid";
import {ModelMappingRules} from "../../../formability/mappingBuilder/enums/model-mapping-rules";
import {RelationType} from "../relationType";
import ModelMappingValue from "./modelMappingValue";
import {Hash} from "~/interfaces/hashInterfaces";
import store from "../../state/store";
import {IPeekerOptions} from "~/services/peeker";
import MappingBuilderModel from "../tracked/mappingBuilderModel";
import ElementHandler from "../../services/elementHandler";
import {ObjectHelper} from "~/helpers/objectHelper";
import {
  IMappingRelationIdentifierCreateOpts,
  IModelMapping, IModelMappingContainer,
  IOriginWrapper, TMappingOriginType,
  TModelMappingOriginType
} from "~/interfaces/mappingInterfaces";
import {ModelMappingValuesType} from "../../../formability/mappingBuilder/enums/modelMappingValuesType";
import TrackedModel from "../tracked/trackedModel";
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import ModelMappingOriginFactory, {ModelMappingOriginTypes} from "~/models/mapping/modelMappingOriginFactory";
import Validatable from "~/mixins/validatable";
import ModelMappingValidation from "~/models/navigableValidations/modelMappingValidation";
import {ModelType} from "~/enums/modelType";
import * as _ from 'lodash';
import {ObjectModelBuiltinPropertyType} from "~/enums/objectModelBuiltinPropertyType";
import {EnumHelper} from "~/helpers/enumHelper";
import MappingOrigin from "~/models/mapping/mappingOrigin";
import MappingOriginFactory, {MappingOriginKlassTypes} from "~/models/mapping/mappingOriginFactory";
import ModelMappingHandler from "../../../formability/mappingBuilder/services/modelMappingHandler";
import Mapping from "~/models/mapping/mapping";
import RelationIdentifier from "~/models/mapping/relationIdentifier";
import {SalesforceModelTypes} from '~/enums/salesforceEnums'

export default class ModelMapping extends Validatable(MappingBuilderModel) implements IModelMapping {

  source_identifier: string;
  source_type: TModelMappingOriginType;

  destination_identifier: string;
  destination_type: TModelMappingOriginType;

  lookup_keys: Array<string>;

  rule: ModelMappingRules;
  enable_preserve_ownership: boolean;
  prepopulate_from_record_id: boolean;
  //Used for self referential relationships in relationship identifier model mappings
  inverse_relation: boolean;

  sort_order: number;
  destination_root_relation_identifier: RelationIdentifier

  model_mapping_values: Hash<ModelMappingValue> = {};

  source: MappingOrigin;
  destination: MappingOrigin;

  private _hasFetchedNestedSourceObjectModelRelations: boolean = false
  private _hasFetchedNestedDestinationObjectModelRelations: boolean = false

  get TRACKED_ARRAYS(): string[] {
    return ["lookup_keys"];
  }

  get mapping(): Mapping {
    if (this.parent instanceof Mapping) {
      return this.parent
    } else {
      return this.parent.mapping
    }
  }

  get modelMappingValuesArray(): ModelMappingValue[] {
    return ObjectHelper.toArray(this.model_mapping_values)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  _modelMappingValueHandler: ElementHandler<ModelMappingValue> = new ElementHandler(this, ModelMappingValue, 'model_mapping_values');
  get modelMappingValueHandler(): ElementHandler<ModelMappingValue> {
    return this._modelMappingValueHandler;
  }

  _modelMappingHandler: ModelMappingHandler<ModelMapping> = new ModelMappingHandler(this, ModelMapping, 'model_mappings');
  get modelMappingHandler(): ModelMappingHandler {
    return this._modelMappingHandler;
  }

  get isLast(): boolean {
    return this.sort_order === (<ModelMapping>this.parent).nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  private get isSecond(): boolean {
    return this.sort_order === 1
  }

  get canMoveUp(): boolean {
    // can never move up if is first item
    if (this.isFirst) return false
    // top-level model mappings have a static 1st one, so 2nd is the new "first"
    if (!this.isNested) return !this.isSecond
    return true
  }

  get canMoveDown(): boolean {
    return !this.isLast
  }

  get canBeFormPrepopulationMapping(): boolean {
    return (this.source_type === ModelType.ObjectModel && (this.destination_type === ModelType.Form || this.destination_type === ModelType.FormElement) ||
      this.source_type === ModelType.RelationIdentifier && (this.destination_type === ModelType.Form || this.destination_type === ModelType.FormElement))
  }

  get isFormPrepopulationMapping(): boolean {
    return this.canBeFormPrepopulationMapping && this.rule === ModelMappingRules.FormPrepopulation
  }

  get isObjectModelToFormPrepopulation(): boolean {
    return this.rule === ModelMappingRules.FormPrepopulation &&
    this.source_type === ModelType.ObjectModel &&
    this.destination_type === ModelType.Form
  }

  get isObjectModelToSf(): boolean {
    return this.source_type === ModelType.ObjectModel &&
      SalesforceModelTypes.includes(this.destination_type)
  }

  get isRelationIdentifierToSf(): boolean {
    return this.source_type === ModelType.RelationIdentifier &&
      SalesforceModelTypes.includes(this.destination_type)
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.modelMappingHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.modelMappingHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.modelMappingHandler.recalculateElementPositions();
  }

  static repo: IRepo<ModelMapping> = new Repo<ModelMapping>('modelMappings', ModelMapping, {});
  repo: IRepo<this>;

  /**
   *
   * @param mapping - parent to which this model mappings belongs to
   * @param {IModelMappingContainer} parent - model mapping can be nested in mapping or another model mapping
   * @param type - Not used
   * @param {IOriginWrapper} sourceParent - MappingOrigin for source side
   * @param {IOriginWrapper} destinationParent - MappingOrigin for source side
   * @param position - Its order amongst the model mappings
   */
  initialize(mapping, parent, type, {sourceParent, destinationParent, position}: {sourceParent: IOriginWrapper, destinationParent: IOriginWrapper, position: number}): this {
    this.parent = parent;

    this.source_identifier = sourceParent.rootIdentifier;
    this.source_type = sourceParent.type;
    this.destination_identifier = destinationParent.rootIdentifier;
    this.destination_type = destinationParent.type;

    this.defineProperty('sort_order', position);
    this.defineProperty('enable_preserve_ownership', false);
    this.defineProperty('prepopulate_from_record_id', true);
    this.defineProperty("client_id", Guid.create());
    this.defineProperty("rule", null);
    this.defineProperty('lookup_keys', TrackedArrayProxy.create(Object.assign([], {parent: this, key: 'lookup_keys'})));
    this.defineProperty('destination_root_relation_identifier', null)

    this.buildMappingOrigins()
    return this
  }

  addToRepo(): void {
    ModelMapping.repo.add(this);
  }

  _destinationOrigin: ModelMappingOriginTypes
  get destinationOrigin(): ModelMappingOriginTypes {
    if (!this._destinationOrigin) {
      this._destinationOrigin = ModelMappingOriginFactory.create(this.destination_type, this.destination_identifier, this, this.parent.destination)
    }
    return this._destinationOrigin
  }

  _sourceOrigin: ModelMappingOriginTypes
  get sourceOrigin(): ModelMappingOriginTypes {
    if (!this._sourceOrigin) {
      this._sourceOrigin = ModelMappingOriginFactory.create(this.source_type, this.source_identifier, this, this.parent.source)
    }
    return this._sourceOrigin
  }

  cleanUpLookupKeys() {
    const clientIds = Object.values(this.model_mapping_values).map(v => v.client_id);
    const index = Array.from(this.lookup_keys)
    index.forEach(ind => {
      if (!clientIds.includes(ind)) {
        _.remove(this.lookup_keys, ind);
      }
    });
  }

  // determine whether or not the source can have/ has lookup keys
  get hasIndexedReferences() {
    let hasSome = false;
    if ([ModelType.Form, ModelType.ObjectModel, ModelType.FormElement, ModelType.RelationIdentifier].includes(this.source_type)) {
      if (_.some(this.model_mapping_values, (val, key, coll) => {
        return val.source?.isIndexed;
      })){
        hasSome = true
      }
    } else if (SalesforceModelTypes.includes(this.source_type)){
      hasSome = true;
    }
    return hasSome;
  }

  get modelMappingValuesSourceClientIds(): string[] {
    return Object.keys(this.model_mapping_values).map(key => this.model_mapping_values[key].source_value_identifier)
  }

  // returns an array of arrays containing dynamic fields used in model mapping value formulas
  get modelMappingValuesFormulasDynamicFieldsClientIds(): RegExpMatchArray[] {
    return Object.keys(this.model_mapping_values).map(key => {
      if (this.model_mapping_values[key].type === ModelMappingValuesType.Formula) {
        return this.model_mapping_values[key].mapping_formula.match(/f_[a-z0-9]{32}/g)
      }
    }).filter(value => value !== undefined);
  }

  isMapped(value: string): boolean {
    const idInFormula = `f_${value.replace(/-/g, '')}`;
    return this.modelMappingValuesSourceClientIds.includes(value) || this.isInFormula(idInFormula)
  }

  isInFormula(value: string): boolean {
    return this.modelMappingValuesFormulasDynamicFieldsClientIds.some((arr: string[]) => {
      if (Array.isArray(arr)) {
        return arr.includes(value);
      } else {
        return false;
      }
    });
  }

  isSingle(sourceOrDestination: 'sourceOrigin' | 'destinationOrigin'): boolean {
    return this[sourceOrDestination]?.isSingle
  }

  static get displayName(): string {
    return 'ModelMapping';
  }

  get collectionKey() {
    return "modelMappings";
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'u_at',
    ];
  }

  get rootName() {
    return 'model_mappings';
  }

  static get rootName(): string {
    return 'model_mappings';
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'source': { type: MappingOrigin, relationType: RelationType.singleObject },
      'destination': { type: MappingOrigin, relationType: RelationType.singleObject },
      'model_mappings': {type: ModelMapping, relationType: RelationType.dictionary},
      'model_mapping_values': {type: ModelMappingValue, relationType: RelationType.dictionary},
      'destination_root_relation_identifier': {
        type: RelationIdentifier,
        relationType: RelationType.singleObject
      },
    };
  }

  createEmbeddedObject = {
    model_mappings: (parent, json): ModelMapping => {
      return new ModelMapping();
    },
    source: (parent, json) => {
      const type: TMappingOriginType = json.friendly_type;
      const klass: MappingOriginKlassTypes = MappingOriginFactory.getClass(type)
      let obj: MappingOrigin

      if (klass) {
        obj = new klass()
        obj._nameInParent = 'source';
      }

      return obj;
    },
    destination: (parent, json) => {
      const type: TMappingOriginType = json.friendly_type;
      const klass: MappingOriginKlassTypes = MappingOriginFactory.getClass(type)
      let obj: MappingOrigin

      if (klass) {
        obj = new klass()
        obj._nameInParent = 'destination';
      }

      return obj;
    },
    destination_root_relation_identifier: (parent, json): RelationIdentifier => {
      return new RelationIdentifier(parent)
        .loadFromJson({
          ...json,
          nameInParent: 'destination_root_relation_identifier',
        });
    },
    model_mapping_values: (parent, json): ModelMappingValue => {
      return new ModelMappingValue().loadFromJson(json, parent);
    },
  };

  model_mappings: Hash<ModelMapping> = {};
  get modelMappingsArray(): ModelMapping[] {
    return ObjectHelper.toArray(this.model_mappings)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  modelMappingValueIdentifiers(whichSide: 'source' | 'destination') {
    return this.extractValueIdentifiers(this.model_mapping_values, whichSide);
  }

  extractValueIdentifiers(modelMappingValues: Hash<ModelMappingValue>, type: 'source' | 'destination'): Array<string> {
    const identifierKey = `${type}_value_identifier`;
    return Object.values(modelMappingValues).filter(e => {
      return e.type === ModelMappingValuesType.Value
    }).map(value => value[identifierKey]);
  }

  initializeEmbedded<T extends ModelMappingValue & TrackedModel | ModelMapping>(parent: ModelMapping, item: T, position?: number) {
    this.placeInDictionary(item, item.rootName, item.parent);
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    return item;
  }

  addModelMappingValues(sourceClientIds: string[]): void {
    sourceClientIds.map(sourceClientId => {
      const mappingValue = this.constructModelMappingValue(ModelMappingValuesType.Value,
        {
          source_value_identifier: sourceClientId,
          source_is_default_property: !_.isNil(EnumHelper.parseStringValue(sourceClientId, ObjectModelBuiltinPropertyType))
        });
      this.placeInDictionary(mappingValue, mappingValue.rootName, mappingValue.parent);
      return mappingValue;
    }).forEach(mappingValue => {
      this.trackCreationDeletion(mappingValue.client_id, null, mappingValue.asJson(), mappingValue);
    });
  }

  createNewModelMappingValue(type: number, json: Partial<ModelMappingValue> = {}): void {
    const mappingValue = this.constructModelMappingValue(type, json);
    this.initializeEmbedded(this, mappingValue);
  }

  constructModelMappingValue(type: number, json: Partial<ModelMappingValue> = {}): ModelMappingValue {
    //constructs without tracking creation/deletion, which will cycle through collection and do all at once
    const attrs = {sort_order: this.nextSortOrderFor(ModelMappingValue.rootName)}
    const mappingValue = new ModelMappingValue();
    mappingValue.initialize(this, this, type, attrs).loadFromJson(json, this);
    this.preassignModelMappingValueDestination(mappingValue);
    return mappingValue;
  }

  preassignModelMappingValueDestination(mappingValue: ModelMappingValue): void {
    if (mappingValue.source_value_identifier === '') return;
    if (this.destinationOrigin.mappingOriginIs(ModelType.SObject) && mappingValue.source?.isUploader) {
      mappingValue.destination_value_identifier = this.destinationOrigin.uploaderValueOptions[0].value;
      mappingValue.target = mappingValue.destination_value_identifier;
    } else if (this.sourceOrigin.mappingOriginIs(ModelType.SObject) && mappingValue.source?.isUploader) {
      mappingValue.target = mappingValue.source_value_identifier;
    }
  }

  get storeName(): string {
    return `mappingStore`;
  }

  // ******************** TRACK CHANGES******************** //

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    store.commit(`${this.storeName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //


  // ******************** VALIDATABLE******************** //
  _validation: ModelMappingValidation<ModelMapping> = new ModelMappingValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  // ******************** VALIDATABLE END******************** //

  get titleOrDefault() {
    return `${this.sourceOrigin.title} Maps to ${this.destinationOrigin.title}`
  }

  // ******************** NESTED MODEL MAPPING ******************** //

  /**
   * Check on whether this model mapping can have nested model mappings
   * Current usage is for #175469582, model mappings for form elements as source where
   * said element has at least one nested repeatable element
   */
  get canHaveNestedModelMappings(): boolean {
    return this.sourceOrigin.sourceCanHaveNestedModelMappings &&
      this.destinationOrigin.destinationCanHaveNestedModelMappings
  }

  get isNested(): boolean {
    return Boolean(this.parentModelMapping)
  }

  get parentModelMapping(): ModelMapping {
    if (this.parent instanceof ModelMapping) return this.parent
  }

  modelMappingSourceOptions(): IOriginWrapper[] {
    return this.sourceOrigin.nestedModelMappingSourceOptions()
  }

  modelMappingDestinationOptions(source: IOriginWrapper): IOriginWrapper[] {
    return this.destinationOrigin.nestedModelMappingDestinationOptions(source)
  }

  async fetchRelations(): Promise<void> {
    const promises = []
    if (this.source) promises.push(this.sourceOrigin.fetchRelations())
    if (this.destination) promises.push(this.destinationOrigin.fetchRelations())
    await Promise.all(promises)
  }

  async buildRelationIdentifiers(): Promise<void> {
    await this.buildSourceRelationIdentifiers()
    await this.buildDestinationRelationIdentifiers()
  }

  async buildSourceRelationIdentifiers(): Promise<void> {
    if (!this._hasFetchedNestedSourceObjectModelRelations && !this.sourceOrigin.hasRelations) {
      await this.sourceOrigin.fetchRelations()
      this._hasFetchedNestedSourceObjectModelRelations = true
    }
    this.sourceOrigin.buildRelationIdentifiers(this.source)
  }

  async buildDestinationRelationIdentifiers(): Promise<void> {
    if (!this._hasFetchedNestedDestinationObjectModelRelations && !this.destinationOrigin.hasRelations) {
      await this.destinationOrigin.fetchRelations()
      this._hasFetchedNestedDestinationObjectModelRelations = true
    }
    this.destinationOrigin.buildRelationIdentifiers(this.destination)
  }

  createModelMapping(source: IOriginWrapper, destination: IOriginWrapper) {
    let newModelMapping = this.createEmbeddedObject.model_mappings(this, {})
    newModelMapping.initialize(this.mapping, this, null, {
      sourceParent: source,
      destinationParent: destination,
      position: this.nextSortOrderFor(ModelMapping.rootName),
    })
    this.initializeEmbedded(this, newModelMapping, this.nextSortOrderFor(ModelMapping.rootName))
    return newModelMapping
  }

  get hasMappingOrigins(): boolean {
    return Boolean(this.source) || Boolean(this.destination)
  }

  createMappingOrigins() {
    if (!this.source) {
      const source = this.buildSourceMappingOrigin()
      this.trackCreation(source);
    }

    if (!this.destination) {
      const destination = this.buildDestinationMappingOrigin()
      this.trackCreation(destination);
    }
  }

  buildMappingOrigins() {
    if (this.hasMappingOrigins) return;
    this.buildSourceMappingOrigin()
    this.buildDestinationMappingOrigin()
  }

  buildSourceMappingOrigin() {
    const origin = this.buildMappingOrigin('source')
    Vue.set(this, 'source', origin)
    return origin
  }

  buildDestinationMappingOrigin() {
    const origin = this.buildMappingOrigin('destination')
    Vue.set(this, 'destination', origin)
    return origin
  }

  private buildMappingOrigin(sourceOrDestination: 'source' | 'destination') {
    const mappingOriginClass = MappingOriginFactory.getClassForModelMapping(this[`${sourceOrDestination}_type`])
    let mappingOrigin = new mappingOriginClass()
    // TODO: Salesforce isn't handled yet for a nested model mapping
    //   see Mapping model for its implementation for inspiration

    mappingOrigin.rootIdentifier = this[`${sourceOrDestination}Origin`].modelIdentifier;
    mappingOrigin.modelIdentifiers = []
    mappingOrigin.relationIdentifiers.removeAll()

    mappingOrigin._nameInParent = sourceOrDestination
    mappingOrigin.parent = this
    return mappingOrigin;
  }

  get sourceCanRelateToRoot(): boolean | null {
    return this.source?.canRelateToRoot
  }

  get destinationCanRelateToRoot(): boolean | null {
    return this.destination?.canRelateToRoot
  }

  createDestinationRootRelationIdentifier(attrs: IMappingRelationIdentifierCreateOpts): RelationIdentifier {
    const ri: RelationIdentifier = this.createEmbeddedObject
      .destination_root_relation_identifier(this, attrs)
    // not sure why I have to do this but time is of the essence
    this.withoutTracking(() => this.destination_root_relation_identifier = ri)
    this.trackCreation(ri)
    return ri
  }
  // ******************** NESTED MODEL MAPPING END ******************** //
}
